/**
 * What the document renders, chosen once from the address (ORDR-63).
 *
 * This app has no router: the shell reads its mode from the pathname
 * (`shell/orderingPack.ts`) and `OrderingContextProvider` reads its door from
 * the address at mount. The three identity addresses are chosen the same way,
 * one level up, because they must render OUTSIDE that provider: inside it, an
 * anonymous visitor at `/login` would get the staff door's notice instead of a
 * sign-in button, and an invitee would get it instead of their invitation.
 * Adding react-router for three addresses would introduce a second idea of
 * routing beside the pack's route table.
 *
 * ORDR-63 brought `/login` and `/auth/callback`. ORDR-61 adds
 * `/invite/<token>/verify`, which is the one the outside world links to: it is
 * the address paio-auth puts in every ordering invitation email. ORDR-189 adds
 * `/auth/link/<token>`, the emailed sign-in link's landing.
 *
 * Every navigation between these screens is a document load (a link, or
 * `window.location.replace` after the callback), so reading the pathname once is
 * correct rather than a shortcut.
 *
 * The organization's brand is read here too, once per document load and above
 * every screen (ORDR-139). Holding it is all this level does: only the customer
 * and admin surfaces draw it, through `BrandedSurface`.
 */

import { useState, type ReactElement } from "react";
import { App } from "./App.js";
import { authScreenFor, inviteTokenFrom, signinLinkTokenFrom } from "./auth/paths.js";
import { RuntimeBrandProvider } from "./brand/RuntimeBrand.js";
import { AuthCallback } from "./screens/AuthCallback.js";
import { InviteVerify } from "./screens/InviteVerify.js";
import { Login } from "./screens/Login.js";
import { SigninLink } from "./screens/SigninLink.js";
import { OrderingContextProvider } from "./session/OrderingContextProvider.js";

export function Root(): ReactElement {
  return (
    <RuntimeBrandProvider>
      <Screen />
    </RuntimeBrandProvider>
  );
}

function Screen(): ReactElement {
  const [pathname] = useState(() => window.location.pathname);
  const [screen] = useState(() => authScreenFor(pathname));

  if (screen === "login") {
    return <Login />;
  }
  if (screen === "callback") {
    return <AuthCallback />;
  }
  if (screen === "invite") {
    // Non-null whenever the screen is "invite": authScreenFor decides by
    // asking this same function. The fallback keeps the type honest without
    // pretending a second source of truth exists.
    return <InviteVerify token={inviteTokenFrom(pathname) ?? ""} />;
  }
  if (screen === "link") {
    // Non-null whenever the screen is "link", for the reason given above.
    return <SigninLink token={signinLinkTokenFrom(pathname) ?? ""} />;
  }
  // The provider decides the door from the address: `/order?site=<uuid>` is a
  // diner and asks the server for nothing; every other address reads the session
  // and then the ordering context before any staff surface renders.
  return (
    <OrderingContextProvider>
      <App />
    </OrderingContextProvider>
  );
}
